import { useState } from 'react';
import { useAppFeatures } from '../app-features/api.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MEDIA_VOCAB_QUERY_KEY,
  listVocab,
  mediaListQueryKey,
  patchMedia,
  deleteMedia,
  downloadMedia,
  type HotelMedia,
  type MediaApiError,
  type MediaPatchBody,
  type Vocab,
} from './api.js';
import type { AiInvolvement, PeopleConsent, RightsBasis } from './types.js';
import {
  AI_INVOLVEMENT_LABEL,
  PEOPLE_CONSENT_LABEL,
  RIGHTS_BASIS_LABEL,
  RIGHTS_CONFIRMED_REF_HINT,
  buildEditedRightsConfirmation,
  showsAiChangeNote,
  showsRightsConfirmedRef,
  showsStockFields,
  fromPeopleRecognizable,
  toPeopleRecognizable,
  type PeopleRecognizableChoice,
} from './media-compliance-fields.js';
import { PeopleRecognizableRadioGroup } from './media-people-radio.js';
import { BLOCKER_REASON, COMPLIANCE_STATUS_LABEL } from './media-compliance-copy.js';
import { MediaBadges } from './media-badges.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Badge } from '../../components/ui/badge.js';
import { Field, FormActions } from '../../components/ui/form.js';
import { Input } from '../../components/ui/input.js';
import { Select } from '../../components/ui/select.js';
import { ZoomableImage } from '../../components/ui/image-lightbox.js';
import { useConfirm } from '../../components/ui/confirm-dialog.js';
import { cn } from '../../lib/utils.js';

/**
 * Turns a `patchMedia` failure into a Hungarian sentence, the same way
 * `describeUploadError` does for `MediaUploadPanel` (`./media-upload-panel.tsx`)
 * — same `MediaApiError` shape off `./api.ts`'s `mediaJson`, but a patch can
 * only ever hit the tag-dictionary or malformed-compliance-payload cases
 * (never `duplicate_image` or `content_type_unsupported`, which are upload-only).
 */
function describeSaveError(err: unknown): string {
  const code = (err as Partial<MediaApiError> | undefined)?.code;
  switch (code) {
    case 'unknown_tag': {
      const tags = (err as MediaApiError).unknownTags ?? [];
      return `Ismeretlen címke: ${tags.join(', ')}`;
    }
    case 'compliance_fields_invalid':
      return 'A megadott adatok érvénytelenek — ellenőrizd a mezőket.';
    default:
      return err instanceof Error && err.message ? err.message : 'A mentés sikertelen.';
  }
}

/**
 * Dictionary-only tag editor: existing tags render as removable chips, a new
 * one can only be added by picking a matching suggestion — there is no
 * "commit whatever you typed" path, unlike `components/ui/tag-input.tsx`.
 * That is the whole point of the closed vocabulary (galéria-revízió spec).
 *
 * An already-archived tag can still sit on the image (someone tagged it
 * `husvet2025` before the category was retired) — it stays in `tags` and
 * renders marked, but the suggestion list excludes archived words so nobody
 * can add a retired tag to a DIFFERENT image from here.
 */
function TagPicker({
  vocab,
  tags,
  onChange,
}: {
  vocab: Vocab;
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const trimmed = query.trim().toLowerCase();
  const suggestions =
    trimmed.length === 0
      ? []
      : vocab.tags
          .filter(
            (t) =>
              t.archivedAt === null &&
              !tags.includes(t.tag) &&
              t.tag.toLowerCase().includes(trimmed),
          )
          .slice(0, 8);

  function addTag(tag: string) {
    if (!tags.includes(tag)) onChange([...tags, tag]);
    setQuery('');
  }

  function removeTag(tag: string) {
    onChange(tags.filter((t) => t !== tag));
  }

  return (
    <div className="space-y-1.5">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => {
            const archived = vocab.tags.find((t) => t.tag === tag)?.archivedAt != null;
            return (
              <Badge
                key={tag}
                tone={archived ? 'neutral' : 'success'}
                className="flex items-center gap-1"
              >
                <span className={cn(archived && 'italic')}>{tag}</span>
                {archived && (
                  <span
                    title="Archivált címke"
                    aria-label="Archivált címke"
                    className="text-[10px]"
                  >
                    (archív)
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`${tag} eltávolítása`}
                  className="text-primary/70 hover:text-primary"
                >
                  ×
                </button>
              </Badge>
            );
          })}
        </div>
      )}
      <Input
        id="mdd-tag-query"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Keress a szótárban…"
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {suggestions.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => addTag(t.tag)}
                className="rounded-pill border border-[var(--app-divider-2)] px-2 py-0.5 text-meta text-[var(--fg-2)] hover:bg-[var(--app-hover)]"
              >
                {t.tag}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * `deleteMedia` failure → Hungarian sentence. `media_in_use` never reaches
 * this: `handleDelete` turns it into the second confirmation instead.
 */
function describeDeleteError(err: unknown): string {
  const code = (err as Partial<MediaApiError> | undefined)?.code;
  if (code === 'not_found') return 'A kép már nincs a galériában.';
  return err instanceof Error && err.message ? err.message : 'A törlés sikertelen.';
}

export function MediaDetailDialog({
  hotelId,
  media,
  onClose,
  onSaved,
  onDeleted,
}: {
  hotelId: string;
  media: HotelMedia;
  onClose: () => void;
  /**
   * Called once a save actually lands. The gallery page uses this to bump
   * its `reloadToken` — a saved tag/compliance change can flip `attachable`
   * or drop the row out of the current filter, and per the controller
   * ruling the grid reloads from page one after any change instead of
   * trying to patch the accumulated list in place.
   */
  onSaved?: () => void;
  /**
   * Called once the picture is gone (after `onClose`'s caller should drop
   * this dialog). The gallery page bumps its `reloadToken` here, for the
   * same reason as `onSaved`.
   */
  onDeleted?: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const vocabQuery = useQuery<Vocab>({
    queryKey: MEDIA_VOCAB_QUERY_KEY,
    queryFn: () => listVocab(),
  });
  const vocab = vocabQuery.data ?? { categories: [], tags: [] };

  // `record` is the last answer the ENGINE gave about this image — the
  // initial prop, then whatever `patchMedia` returns. The status shown below
  // always reads off `record`, never off the form fields: attachable,
  // complianceStatus, blockers and warnings are recomputed server-side, and
  // guessing them here would defeat the entire point of the compliance
  // engine being the single source of truth.
  const [record, setRecord] = useState<HotelMedia>(media);

  const [tags, setTags] = useState<string[]>(media.tags);
  const [rightsBasis, setRightsBasis] = useState<RightsBasis>(media.rightsBasis);
  const [rightsConfirmedRef, setRightsConfirmedRef] = useState(media.rightsConfirmedRef ?? '');
  const [aiInvolvement, setAiInvolvement] = useState<AiInvolvement>(media.aiInvolvement);
  const [aiChangeNote, setAiChangeNote] = useState(media.aiChangeNote ?? '');
  // The record's ACTUAL answer, `null` included — never `?? false`. An old,
  // never-triaged image opens on "Nem tudom", so saving an unrelated edit
  // (a tag, say) sends that null straight back instead of quietly declaring
  // "nobody recognisable here" and clearing the `people_unknown` blocker.
  const [peopleChoice, setPeopleChoice] = useState<PeopleRecognizableChoice>(
    fromPeopleRecognizable(media.peopleRecognizable),
  );
  const [peopleConsent, setPeopleConsent] = useState<PeopleConsent>(media.peopleConsent ?? 'none');
  const [stockPlatform, setStockPlatform] = useState(media.stockPlatform ?? '');
  const [stockTerritory, setStockTerritory] = useState(media.stockTerritory ?? '');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState(media.licenseExpiresAt ?? '');

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showRightsRef = showsRightsConfirmedRef(rightsBasis);
  const showStockFields = showsStockFields(rightsBasis);
  const showAiChangeNote = showsAiChangeNote(aiInvolvement);
  const showPeopleConsent = peopleChoice === 'yes';

  /**
   * Fetched once, when the dialog mounts for this image — NOT on click (fix
   * round 2, task 27). Opening a window after an awaited fetch sits outside
   * the click's user-gesture context, and browsers routinely block exactly
   * that: the download would then silently do nothing, which is worse than
   * the 401 it replaced (that at least showed an error). Fetching up front
   * and rendering a plain `<a href>` sidesteps the popup blocker entirely —
   * there is no script-driven window to block — and gets right-click "save
   * as" and middle-click for free. The endpoint itself still needs the
   * Bearer token (`downloadMedia` goes through the authenticated fetch
   * wrapper), but the pre-signed URL it returns carries its own
   * authorisation and needs no header once it's in the `href`.
   */
  const features = useAppFeatures();
  const complianceOff = features.data?.mediaComplianceMode === 'off';
  const downloadQuery = useQuery({
    queryKey: ['media-download', hotelId, media.id],
    queryFn: () => downloadMedia(hotelId, media.id),
  });

  async function handleSave() {
    if (isSaving) return;
    setIsSaving(true);
    setError(null);
    const patch: MediaPatchBody = {
      tags,
      rightsBasis,
      // The dialog knows the stored pair, so it keeps the original
      // confirmation date unless the reference itself changed (finding 14).
      ...buildEditedRightsConfirmation(rightsBasis, rightsConfirmedRef, {
        rightsConfirmedRef: record.rightsConfirmedRef,
        rightsConfirmedAt: record.rightsConfirmedAt,
      }),
      aiInvolvement,
      aiChangeNote: showAiChangeNote ? aiChangeNote.trim() || null : null,
      peopleRecognizable: toPeopleRecognizable(peopleChoice),
      peopleConsent: showPeopleConsent ? peopleConsent : null,
      stockPlatform: showStockFields ? stockPlatform.trim() || null : null,
      stockTerritory: showStockFields ? stockTerritory.trim() || null : null,
      licenseExpiresAt: showStockFields ? licenseExpiresAt.trim() || null : null,
    };
    try {
      const updated = await patchMedia(hotelId, media.id, patch);
      setRecord(updated);
      // The grid behind this dialog must reflect the new tags/compliance
      // status without a manual reload — same key the bulk bar invalidates.
      void qc.invalidateQueries({ queryKey: mediaListQueryKey(hotelId) });
      onSaved?.();
    } catch (err) {
      setError(describeSaveError(err));
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Two confirmations at most. The first is the ordinary "are you sure". The
   * second appears only when the server answers `media_in_use`: it names how
   * many posts show the picture and asks whether to take it off them too —
   * the server never does that on its own (`?detach=true` is the explicit
   * second step, see `deleteMedia`).
   */
  async function handleDelete() {
    if (isSaving || isDeleting) return;
    const ok = await confirm({
      title: 'Kép törlése',
      message: 'A kép végleg törlődik a galériából. A törlés nem visszavonható.',
      confirmLabel: 'Igen, törlöm',
      tone: 'destructive',
    });
    if (!ok) return;
    setIsDeleting(true);
    setError(null);
    try {
      try {
        await deleteMedia(hotelId, media.id);
      } catch (err) {
        if ((err as Partial<MediaApiError> | undefined)?.code !== 'media_in_use') throw err;
        const count = (err as MediaApiError).postCount ?? 1;
        const detach = await confirm({
          title: 'A képet poszt használja',
          message: `${count} poszt használja ezt a képet. Ha törlöd, onnan is eltűnik, és a posztok kép nélkül maradnak.`,
          confirmLabel: 'Törlés a posztokból is',
          tone: 'destructive',
        });
        if (!detach) return;
        await deleteMedia(hotelId, media.id, { detachFromPosts: true });
      }
      void qc.invalidateQueries({ queryKey: mediaListQueryKey(hotelId) });
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(describeDeleteError(err));
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Dialog open onClose={onClose} size="2xl" title="Kép részletei">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        className="max-h-[78vh] space-y-5 overflow-y-auto pr-1"
      >
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-2">
            <ZoomableImage
              src={media.url}
              alt={media.caption ?? 'Hotel kép'}
              className="h-64 w-full rounded-md bg-[var(--app-divider)] object-contain sm:h-72"
            />
            <div className="flex flex-wrap items-center gap-2">
              {/*
                `MediaBadges` already renders the "unknown"/"blocked" status
                chip when the image is not attachable (and nothing at all for
                a clean, attachable image with no warnings) — reusing it here
                keeps that one place from drifting. The positive "Csatolható"
                state is the one case `MediaBadges` deliberately never shows
                (a plain grid of cards would look noisy if every clean photo
                got a chip), so this dialog adds it itself, straight off
                `record` — the same field the status flips on after a save.
              */}
              {record.attachable && !complianceOff && (
                <Badge tone="success">{COMPLIANCE_STATUS_LABEL.attachable}</Badge>
              )}
              <MediaBadges media={record} />
            </div>
            {!record.attachable && record.blockers.length > 0 && (
              <div className="space-y-1 rounded-md border border-[var(--app-divider)] bg-[var(--app-hover)] p-3">
                <p className="text-body font-medium text-[var(--ink-700)]">Miért nem csatolható:</p>
                <ul className="space-y-0.5 text-body text-[var(--fg-2)]">
                  {record.blockers.map((b) => (
                    <li key={b}>{BLOCKER_REASON[b] ?? b}</li>
                  ))}
                </ul>
              </div>
            )}
            {downloadQuery.isError ? (
              <p className="text-body text-[var(--danger)]">
                {downloadQuery.error instanceof Error && downloadQuery.error.message
                  ? downloadQuery.error.message
                  : 'A letöltés-link lekérése sikertelen.'}
              </p>
            ) : (
              // Present from the first render, never conditionally mounted —
              // disabled (no href, not focusable) while the descriptor is
              // still loading, so the control doesn't jump around as the
              // fetch resolves.
              <a
                {...(downloadQuery.data
                  ? { href: downloadQuery.data.url, download: downloadQuery.data.filename }
                  : { tabIndex: -1 })}
                target="_blank"
                rel="noreferrer noopener"
                aria-disabled={!downloadQuery.data}
                className={cn(
                  'inline-flex h-9 items-center justify-center rounded-md border border-[var(--app-divider-2)] px-3.5 text-body font-medium text-[var(--ink-700)]',
                  downloadQuery.data
                    ? 'hover:bg-[var(--app-hover)]'
                    : 'cursor-not-allowed opacity-50',
                )}
              >
                Letöltés
              </a>
            )}
          </div>

          <div className="space-y-4">
            <Field label="Címkék" htmlFor="mdd-tag-query">
              <TagPicker vocab={vocab} tags={tags} onChange={setTags} />
              {vocabQuery.isError && (
                // Audit I11: without this the picker simply offered no
                // suggestion, which reads as "no such tag", not as a failure.
                <p
                  role="alert"
                  className="mt-1.5 flex flex-wrap items-center gap-2 text-meta text-[var(--danger)]"
                >
                  A címkeszótár nem tölthető be.
                  <Button size="sm" variant="outline" onClick={() => void vocabQuery.refetch()}>
                    Újra
                  </Button>
                </p>
              )}
            </Field>

            {!complianceOff && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Jogalap" htmlFor="mdd-rights-basis">
                  <Select
                    id="mdd-rights-basis"
                    value={rightsBasis}
                    onChange={(e) => setRightsBasis(e.target.value as RightsBasis)}
                  >
                    {(Object.keys(RIGHTS_BASIS_LABEL) as RightsBasis[]).map((v) => (
                      <option key={v} value={v}>
                        {RIGHTS_BASIS_LABEL[v]}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="AI-érintettség" htmlFor="mdd-ai-involvement">
                  <Select
                    id="mdd-ai-involvement"
                    value={aiInvolvement}
                    onChange={(e) => setAiInvolvement(e.target.value as AiInvolvement)}
                  >
                    {(Object.keys(AI_INVOLVEMENT_LABEL) as AiInvolvement[]).map((v) => (
                      <option key={v} value={v}>
                        {AI_INVOLVEMENT_LABEL[v]}
                      </option>
                    ))}
                  </Select>
                </Field>

                {showRightsRef && (
                  <Field
                    label="Megerősítés hivatkozása"
                    htmlFor="mdd-rights-ref"
                    hint={RIGHTS_CONFIRMED_REF_HINT}
                  >
                    <Input
                      id="mdd-rights-ref"
                      value={rightsConfirmedRef}
                      onChange={(e) => setRightsConfirmedRef(e.target.value)}
                    />
                  </Field>
                )}

                {showStockFields && (
                  <>
                    <Field label="Stock platform" htmlFor="mdd-stock-platform">
                      <Input
                        id="mdd-stock-platform"
                        value={stockPlatform}
                        onChange={(e) => setStockPlatform(e.target.value)}
                        placeholder="pl. Shutterstock"
                      />
                    </Field>
                    <Field label="Terület" htmlFor="mdd-stock-territory">
                      <Input
                        id="mdd-stock-territory"
                        value={stockTerritory}
                        onChange={(e) => setStockTerritory(e.target.value)}
                        placeholder="pl. világ / EU / HU"
                      />
                    </Field>
                    <Field label="Licenc lejárata" htmlFor="mdd-license-expires">
                      <Input
                        id="mdd-license-expires"
                        type="date"
                        value={licenseExpiresAt}
                        onChange={(e) => setLicenseExpiresAt(e.target.value)}
                      />
                    </Field>
                  </>
                )}

                {showAiChangeNote && (
                  <Field label="Mi módosult?" htmlFor="mdd-ai-change-note">
                    <Input
                      id="mdd-ai-change-note"
                      value={aiChangeNote}
                      onChange={(e) => setAiChangeNote(e.target.value)}
                      placeholder="pl. háttér cserélve, tárgy eltávolítva"
                    />
                  </Field>
                )}

                <PeopleRecognizableRadioGroup
                  namePrefix="mdd"
                  value={peopleChoice}
                  onChange={setPeopleChoice}
                />

                {showPeopleConsent && (
                  <Field label="Hozzájárulás" htmlFor="mdd-people-consent">
                    <Select
                      id="mdd-people-consent"
                      value={peopleConsent}
                      onChange={(e) => setPeopleConsent(e.target.value as PeopleConsent)}
                    >
                      {(Object.keys(PEOPLE_CONSENT_LABEL) as PeopleConsent[]).map((v) => (
                        <option key={v} value={v}>
                          {PEOPLE_CONSENT_LABEL[v]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
              </div>
            )}

            {error && <p className="text-body text-[var(--danger)]">{error}</p>}

            <FormActions>
              {/*
                Left of the save pair, in the soft danger tone: the ordinary
                way to take one picture out of the library. (Many at once:
                the gallery's select mode + bulk bar.) The hard-red tone is
                reserved for the confirmation dialog's own button.
              */}
              <Button
                type="button"
                variant="destructiveSoft"
                className="mr-auto"
                disabled={isSaving || isDeleting}
                onClick={() => void handleDelete()}
              >
                {isDeleting ? 'Törlés…' : 'Törlés'}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Mégse
              </Button>
              <Button type="submit" disabled={isSaving || isDeleting}>
                {isSaving ? 'Mentés…' : 'Mentés'}
              </Button>
            </FormActions>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
